<template>
  <v-container>
    <!-- Header -->
    <v-row justify="space-between" align="center" class="mb-6">
      <v-col cols="12" md="5">
        <h2 class="text-h4 font-weight-bold">Bulk Image Upload</h2>
      </v-col>
      <v-col cols="12" md="4" class="d-flex justify-end align-center" style="gap: 8px;">
        <v-btn color="primary" @click="showDialog = true">
          <v-icon left>mdi-information-outline</v-icon>
          Upload Instructions
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
  
      <v-col cols="12" md="3">
        <v-select v-model="userType" :items="userTypes" label="Select User Type" outlined dense clearable
          prepend-icon="mdi-account-group" :disabled="isLoading" />
      </v-col>
      <v-col cols="12" md="3" v-if="userType === 'Student'">
        <v-select v-model="selectedClass" :items="classStore.getClassDropdownItems" item-title="title"
          item-value="value" label="Select Class" outlined dense prepend-icon="mdi-google-classroom"
          :disabled="isLoading || !userType" />
      </v-col>
      <v-col cols="12" md="2">
        <v-btn color="primary" @click="openUploaderDialog">
          Upload Images
        </v-btn>
      </v-col>

      <v-col cols="12" md="2">
        <v-btn color="success" @click="checkData"
          :disabled="isLoading || !userType || (userType === 'Student' && !selectedClass) || !uploadedImages || uploadedImages.length === 0"
          block large>
          <v-icon left>mdi-check-circle-outline</v-icon>
          Check Matches
        </v-btn>
      </v-col>

      <v-col cols="12" md="2">
        <v-btn color="primary" v-if="isChecked" @click="uploadImages" block large :disabled="isLoading">
          <v-icon left>mdi-cloud-upload-outline</v-icon>
          Upload
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-row class="mb-4 mr-2 justify-end">
        <UploadProgress v-if="uploadProgress > 0 && uploadProgress < 100" :progress="uploadProgress"
          :uploadedCount="uploadedFilesCount" :totalCount="totalFilesToUpload" />
      </v-row>
      <ImageUploader ref="uploadDropzone" :userType="userType" :isLoading="isLoading"
        @files-updated="updateUploadedImages" style="display: none" />
    </v-row>

    <!-- <v-overlay :value="isLoading" absolute opacity="0.3">
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay> -->

    <MatchedItems v-if="isChecked" :all-items="allItems" :user-type="userType" :matched-count="matchedCount"
      :unmatched-count="unmatchedCount" @reset="resetForm" />

    <InstructionsDialog v-model="showDialog" />
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useClassStore } from '@/Store/className.js'
import { apiConfig } from '@/config/apiConfig'
import Swal from "sweetalert2"
import { useTheme } from '@/composables/useThemes'

// Components
import ImageUploader from '@/components/Apps/UploadFile/ImageUploader.vue'
import MatchedItems from '@/components/Apps/UploadFile/MatchedItems.vue'
import UploadProgress from '@/components/Apps/UploadFile/UploadProgress.vue'
import InstructionsDialog from '@/components/Apps/UploadFile/InstructionsDialog.vue'


const { isDarkTheme } = useTheme()
const classStore = useClassStore()

// Reactive state
const userType = ref('')
const userTypes = ['Student', 'Employee']
const selectedClass = ref('')
const studentList = ref([])
const employeeList = ref([])
const showDialog = ref(false)
const isLoading = ref(false)
const isChecked = ref(false)
const uploadProgress = ref(0)
const totalFilesToUpload = ref(0)
const uploadedFilesCount = ref(0)
const uploadDropzone = ref(null)
const uploadedImages = ref([])

const matchedItems = computed(() => {
  const list = userType.value === 'Student' ? studentList.value : employeeList.value
  return list.filter(item => item._matched)
})

// const matchedItemsCount = computed(() => matchedItems.value.length)

// Methods
const resetForm = () => {
  selectedClass.value = ''
  studentList.value = []
  employeeList.value = []
  isChecked.value = false;
  uploadProgress.value = 0
  uploadedFilesCount.value = 0
  if (uploadDropzone.value) {
    uploadDropzone.value.clearFiles()
  }
}

watch(userType, () => {
  resetForm()
})

watch(showDialog, async (val) => {

})

const updateUploadedImages = (files) => {
  uploadedImages.value = files.map(f => f.file.name)
}

const openUploaderDialog = () => {
  uploadDropzone.value?.openFileDialog()
}

const fetchStudents = async (id) => {
  isLoading.value = true
  try {
    studentList.value = await classStore.fetchStudentsByClassId(id)
    studentList.value.forEach(student => {
      student._assignedImage = null
      student._matched = false
    })
  } catch (err) {
    console.error('Error fetching students:', err)
    showError('Failed to fetch students. Please try again.')
  } finally {
    isLoading.value = false
  }
}

const fetchEmployees = async () => {
  isLoading.value = true
  try {
    const accessToken = localStorage.getItem("accessToken")
    const response = await fetch(`${apiConfig.baseUrl}/employee/`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) throw new Error('Failed to fetch employees')

    employeeList.value = await response.json()
    employeeList.value.forEach(emp => {
      emp._assignedImage = null
      emp._matched = false
    })
  } catch (err) {
    console.error('Error fetching employees:', err)
    showError('Failed to fetch employees. Please try again.')
  } finally {
    isLoading.value = false
  }
}

const checkData = async () => {
  if (!validateForm()) return;

  isLoading.value = true;
  try {
    if (userType.value === 'Student') {
      await fetchStudents(selectedClass.value);
    } else {
      await fetchEmployees();
    }

    const files = uploadDropzone.value.getFiles();
    const list = userType.value === 'Student' ? studentList.value : employeeList.value;

    list.forEach(item => {
      item._matched = false;
      item._newImageFile = null;
      item._previewUrl = null;
    });

    files.forEach(file => {
      const imageName = file.filename.split('.')[0]?.trim();
      if (!imageName) return;

      const found = list.find(item => {
        const identifier = userType.value === 'Student' ? item.roll_no : item.contact;
        return String(identifier).trim() === imageName;
      });

      if (found) {
        found._previewUrl = URL.createObjectURL(file.file);
        found._newImageFile = file.file;
        found._matched = true;
      }
    });

    isChecked.value = true;

    // if (matchedItemsCount.value === 0) {
    if (matchedItems.value.length === 0) {
      showWarning('No matches found. Please check your file names.');
    }
  } catch (error) {
    console.error('Error checking data:', error);
    showError('Failed to process files. Please try again.');
  } finally {
    isLoading.value = false;
  }
};

const uploadImages = async () => {
  if (!validateForm()) return

  if (matchedItems.value.length === 0) {
  // if (matchedItemsCount.value === 0) {
    showError('No matched items to upload')
    return
  }

  isLoading.value = true
  uploadProgress.value = 0
  totalFilesToUpload.value = matchedItems.value.length
  // totalFilesToUpload.value = matchedItemsCount.value
  uploadedFilesCount.value = 0

  const accessToken = localStorage.getItem("accessToken")
  const endpoint = userType.value === 'Student' ? 'student' : 'employee'

  try {
    const batchSize = 3
    for (let i = 0; i < matchedItems.value.length; i += batchSize) {
      const batch = matchedItems.value.slice(i, i + batchSize)

      await Promise.all(batch.map(async (item) => {
        const formData = new FormData()
        const file = item._newImageFile
        const ext = file.name.split('.').pop()
        const identifier = userType.value === 'Student' ? item.roll_no : item.contact
        const customName = `${identifier}.${ext}`

        formData.append('file', file)
        formData.append('data', JSON.stringify([{
          id: item.student,
          imageName: customName
        }]))

        try {
          const response = await fetch(`${apiConfig.baseUrl}/${endpoint}/`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            body: formData
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.message || 'Upload failed')
          }

          uploadedFilesCount.value++
          uploadProgress.value = Math.round((uploadedFilesCount.value / totalFilesToUpload.value) * 100)
        } catch (error) {
          console.error(`Error uploading image for ${identifier}:`, error)
        }
      }))

      await new Promise(resolve => setTimeout(resolve, 300))
    }

    showSuccess(`${uploadedFilesCount.value} images uploaded successfully!`)
    resetForm()
  } catch (error) {
    console.error('Upload error:', error)
    showError('An error occurred during upload. Please try again.')
  } finally {
    isLoading.value = false
    userType.value = ''
  }
}


// Add these computed properties
const unmatchedItems = computed(() => {
  const list = userType.value === 'Student' ? studentList.value : employeeList.value
  return list.filter(item => !item._matched)
})

// Change computed properties
const allItems = computed(() => {
  const list = userType.value === 'Student' ? studentList.value : employeeList.value
  return list.map(item => ({
    ...item,
    _status: item._matched ? 'matched' : 'unmatched'
  }))
})

const matchedCount = computed(() => matchedItems.value.length)
const unmatchedCount = computed(() => allItems.value.length - matchedCount.value)
const validateForm = () => {
  if (!userType.value) {
    showError('Please select a user type')
    return false
  }

  if (userType.value === 'Student' && !selectedClass.value) {
    showError('Please select a class')
    return false
  }

  const files = uploadDropzone.value?.getFiles() || []
  if (files.length === 0) {
    showError('Please upload at least one image')
    return false
  }

  return true
}

// Notification helpers
const showError = (message) => {
  Swal.fire({
    icon: 'error',
    title: 'Error',
    text: message,
    background: isDarkTheme.value ? '#333' : '#fff',
    color: isDarkTheme.value ? '#fff' : '#000',
  })
}

const showSuccess = (message) => {
  Swal.fire({
    icon: 'success',
    title: 'Success',
    text: message,
    background: isDarkTheme.value ? '#333' : '#fff',
    color: isDarkTheme.value ? '#fff' : '#000',
  })
}

const showWarning = (message) => {
  Swal.fire({
    icon: 'warning',
    title: 'Warning',
    text: message,
    background: isDarkTheme.value ? '#333' : '#fff',
    color: isDarkTheme.value ? '#fff' : '#000',
  })
}

// Lifecycle hooks
onMounted(async () => {
  try {
    await classStore.fetchClassInfoList()
  } catch (err) {
    console.error('Error fetching class list:', err)
    showError('Failed to load class list. Please refresh the page.')
  }
})
</script>